/**
 * StatusBadge - Reusable status badge component
 *
 * Used for displaying task, PR, project, and integration statuses with consistent styling
 */

import type { TaskStatus, PullRequestStatus, ProjectStatus, IntegrationStatus, PRReviewStatus } from '@resolve-ai/shared';
import { TaskStatusLabels, PullRequestStatusLabels, ProjectStatusLabels, TaskStatuses, PullRequestStatuses, ProjectStatuses, PRReviewStatuses, PRReviewStatusLabels } from '@resolve-ai/shared';

// Union of all supported status types
type StatusVariant = TaskStatus | PullRequestStatus | ProjectStatus | IntegrationStatus | PRReviewStatus;

interface StatusBadgeProps {
  status: StatusVariant;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  showDot?: boolean;
}

// Integration status labels mapping
const IntegrationStatusLabels: Record<IntegrationStatus, string> = {
  connected: 'Connected',
  not_connected: 'Not Connected',
  error: 'Error',
  expired: 'Expired',
};

const getStatusColor = (status: StatusVariant): string => {
  // Emerald: success states
  if (status === TaskStatuses.COMPLETED || status === PullRequestStatuses.OPEN || status === ProjectStatuses.ACTIVE || status === 'connected' || status === PRReviewStatuses.APPROVED) {
    return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400';
  }

  // Amber: warning/in-progress states
  if (status === TaskStatuses.IN_PROGRESS || status === ProjectStatuses.INACTIVE || status === PRReviewStatuses.PARTIALLY_APPROVED) {
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-400';
  }

  // Red: error/failed states
  if (status === TaskStatuses.FAILED || status === 'error' || status === 'expired' || status === PRReviewStatuses.REJECTED) {
    return 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400';
  }

  // Purple: merged
  if (status === PullRequestStatuses.MERGED) {
    return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
  }

  // Gray: neutral states (pending, closed, archived, not_connected)
  return 'bg-gray-100 text-[#444444] dark:bg-gray-700 dark:text-gray-300';
};

export function StatusBadge({ status, label, size = 'md',showDot = true }: StatusBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-0.5',
    md: 'px-2 py-1',
    lg: 'p-2',
  };


  const getStatusLabel = (status: StatusVariant): string => {
    // Use custom label if provided
    if (label) return label;

    // Map status to appropriate label
    if (status in TaskStatusLabels) {
      return TaskStatusLabels[status as TaskStatus];
    }
    if (status in PullRequestStatusLabels) {
      return PullRequestStatusLabels[status as PullRequestStatus];
    }
    if (status in ProjectStatusLabels) {
      return ProjectStatusLabels[status as ProjectStatus];
    }
    if (status in IntegrationStatusLabels) {
      return IntegrationStatusLabels[status as IntegrationStatus];
    }
    if (status in PRReviewStatusLabels) {
      return PRReviewStatusLabels[status as PRReviewStatus];
    }
    return status;
  };
  const getDotColor = (status: StatusVariant): string => {
    if (
        status === TaskStatuses.COMPLETED ||
        status === PullRequestStatuses.OPEN ||
        status === ProjectStatuses.ACTIVE ||
        status === 'connected' ||
        status === PRReviewStatuses.APPROVED
    ) {
      return 'bg-green-500';
    }

    if (
        status === TaskStatuses.IN_PROGRESS ||
        status === ProjectStatuses.INACTIVE ||
        status === PRReviewStatuses.PARTIALLY_APPROVED
    ) {
      return 'bg-yellow-500';
    }

    if (
        status === TaskStatuses.FAILED ||
        status === 'error' ||
        status === 'expired' ||
        status === PRReviewStatuses.REJECTED
    ) {
      return 'bg-red-600';
    }

    if (status === PullRequestStatuses.MERGED) {
      return 'bg-purple-500';
    }

    return 'bg-gray-400';
  };
  return (
    <p className={`${sizeClasses[size]} w-max flex gap-[0.38rem] items-center rounded-lg ${getStatusColor(status)}`}>
      {showDot && <span className={`rounded-full w-1.5 h-1.5 ${getDotColor(status)}`}/>}
      <span className={'font-caption'}>
        {getStatusLabel(status)}
      </span>
    </p>
  );
}
